import { PowerOfAttorneyFlags, PowerOfAttorneyModel } from "api";
import { Datetime, Field } from "components/shared/form";
import { dateToView, formatDate } from "helpers/date";
import { isPaperPowerOfAttorney } from "helpers/powerOfAttorney";
import { guid, requiredField } from "helpers/validators";
import React, { useState } from "react";
import { applyPowerOfAttorney, removePowerOfAttorney } from "sources/profile/user";

type FormState = {
    externalId: string;
    issueDate: string;
};

type Props = {
    editable: boolean;
    modal: (params: {}) => void;
    powerOfAttorneys?: PowerOfAttorneyModel[];
    update: () => Promise<unknown>;
};

const EMPTY_FORM: FormState = {
    externalId: "",
    issueDate: "",
};

const maxExternalIdLength = (value = "") =>
    value.length <= 72 ? "" : "Номер доверенности не должен превышать 72 символа";
const requiredExternalId = (value = "") => requiredField(value.trim());

const PowerOfAttorneys = ({ editable, modal, powerOfAttorneys = [], update }: Props) => {
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [isLoading, setIsLoading] = useState(false);
    const externalIdValidators = [requiredExternalId, maxExternalIdLength, guid];
    const isValid =
        !externalIdValidators.some((validator) => validator(form.externalId)) &&
        !requiredField(form.issueDate) &&
        !isLoading;

    const setValue = ({ name, value }: { name: keyof FormState; value: string }) =>
        setForm((current) => ({ ...current, [name]: value }));

    const addPowerOfAttorney = () => {
        if (!isValid) {
            return;
        }

        setIsLoading(true);
        applyPowerOfAttorney({
            externalId: form.externalId.trim(),
            issueDate: formatDate(form.issueDate),
            intFlags: PowerOfAttorneyFlags.None,
        })
            .then((result) => {
                if (!result) {
                    return null;
                }

                setForm(EMPTY_FORM);
                return update();
            })
            .finally(() => setIsLoading(false));
    };

    const confirmRemove = (powerOfAttorney: PowerOfAttorneyModel) =>
        modal({
            name: "confirm",
            title: "Удаление доверенности",
            text: `Удалить доверенность «${powerOfAttorney.externalId}»?`,
            buttons: { confirm: "Удалить", cancel: "Отменить" },
            action: () =>
                removePowerOfAttorney({ id: powerOfAttorney.id }).then((result) => (result ? update() : result)),
        });

    return (
        <div className="card card-body mb-3">
            <div className="row">
                <div className="col-sm-4">
                    <h5>Доверенности</h5>
                    <p className="small text-muted mb-sm-0">
                        Доверенность можно выбрать при формировании титулов ЭЗЗ и ЭТрН.
                    </p>
                </div>
                <div className="col-sm-8">
                    {powerOfAttorneys.length > 0 ? (
                        <div className="mb-3">
                            <table className="table table-sm mb-0">
                                <thead>
                                    <tr>
                                        <th>Тип</th>
                                        <th>Номер / GUID</th>
                                        <th>Дата</th>
                                        {editable && <th aria-label="Действия" />}
                                    </tr>
                                </thead>
                                <tbody>
                                    {powerOfAttorneys.map((powerOfAttorney) => (
                                        <tr key={powerOfAttorney.id}>
                                            <td>{isPaperPowerOfAttorney(powerOfAttorney) ? "Бумажная" : "МЧД"}</td>
                                            <td className="text-break">{powerOfAttorney.externalId}</td>
                                            <td className="text-nowrap">
                                                {powerOfAttorney.issueDate
                                                    ? dateToView(powerOfAttorney.issueDate)
                                                    : "—"}
                                            </td>
                                            {editable && (
                                                <td className="text-right">
                                                    <button
                                                        type="button"
                                                        className="btn btn-xs btn-outline-danger"
                                                        onClick={() => confirmRemove(powerOfAttorney)}
                                                    >
                                                        Удалить
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-muted mb-3">Доверенности не добавлены</div>
                    )}

                    {editable && (
                        <div className={isLoading ? "loading loading--small" : ""}>
                            <Field
                                label="GUID доверенности"
                                inputProps={{
                                    name: "externalId",
                                    maxLength: 72,
                                    placeholder: "00000000-0000-0000-0000-000000000000",
                                }}
                                value={form.externalId}
                                onChange={setValue}
                                validators={externalIdValidators}
                                required
                                labelWrapper="col-12 col-form-label col-form-label-sm text-muted"
                                fieldWrapper="col-12"
                                bWrapper="row mb-2"
                            />
                            <Datetime
                                label="Дата выдачи доверенности"
                                inputProps={{ name: "issueDate", type: "date" }}
                                value={form.issueDate}
                                onChange={setValue}
                                validators={[requiredField]}
                                required
                                inPortal
                                labelWrapper="col-12 col-form-label col-form-label-sm text-muted"
                                fieldWrapper="col-12"
                                bWrapper="row mb-3"
                            />
                            <button
                                type="button"
                                className="btn btn-brand btn-sm"
                                disabled={!isValid}
                                onClick={addPowerOfAttorney}
                            >
                                Добавить доверенность
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PowerOfAttorneys;
